import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { IonBadge, IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  albumsOutline,
  barbellOutline,
  bookOutline,
  briefcaseOutline,
  busOutline,
  businessOutline,
  calendarOutline,
  callOutline,
  carOutline,
  carSportOutline,
  cardOutline,
  checkboxOutline,
  chevronDownOutline,
  chevronForwardOutline,
  chevronUpOutline,
  colorPaletteOutline,
  cashOutline,
  powerOutline,
  pulseOutline,
  chatbubblesOutline,
  clipboardOutline,
  cloudDownloadOutline,
  cloudUploadOutline,
  constructOutline,
  createOutline,
  cubeOutline,
  desktopOutline,
  documentOutline,
  documentTextOutline,
  documentsOutline,
  flashOutline,
  funnelOutline,
  gitBranchOutline,
  gitCompareOutline,
  gitNetworkOutline,
  gridOutline,
  globeOutline,
  hammerOutline,
  sparklesOutline,
  idCardOutline,
  imagesOutline,
  checkmarkDoneOutline,
  keyOutline,
  keypadOutline,
  linkOutline,
  listOutline,
  logInOutline,
  lockClosedOutline,
  locationOutline,
  mapOutline,
  mailOutline,
  megaphoneOutline,
  menuOutline,
  navigateOutline,
  speedometerOutline,
  notificationsOutline,
  optionsOutline,
  peopleCircleOutline,
  peopleOutline,
  personOutline,
  pricetagOutline,
  pricetagsOutline,
  printOutline,
  receiptOutline,
  repeatOutline,
  ribbonOutline,
  scaleOutline,
  sendOutline,
  shieldOutline,
  trendingUpOutline,
  swapHorizontalOutline,
  starOutline,
  toggleOutline,
  trashOutline,
  walletOutline,
  waterOutline,
} from 'ionicons/icons';

import { NavBadgeService } from '../../core/nav/nav-badge.service';
import { NavigationPreferenceService } from '../../core/navigation/navigation-preference.service';
import { ViewportService } from './viewport.service';

/** `w-64` — the width the sidebar shipped with, and what a reset returns to. */
const DEFAULT_WIDTH = 256;

/** Narrow enough to be mostly icons; wide enough for the longest label ("Material order statuses"). */
const MIN_WIDTH = 176;
const MAX_WIDTH = 560;

const WIDTH_KEY = 'nav-sidebar-width';
const COLLAPSED_KEY = 'nav-sidebar-collapsed';

/**
 * The sidebar itself: the permission-filtered menu, its per-category collapse state, and the drag
 * handle that sets its width. The shell (MainLayoutComponent) still owns *whether* it is showing —
 * this owns what it looks like when it is.
 *
 * The menu is long (nine sections, ~60 items), so both knobs exist for the same reason: a user who
 * lives on two of those sections should not have to scroll past the other seven, or surrender 16rem
 * of a laptop screen to do it. Width and collapsed sections are remembered per browser, because a
 * preference you have to re-set every morning is not a preference.
 *
 * Width applies on WIDE screens only. Below `md` the sidebar overlays the page as a drawer, where a
 * user-chosen width means little and a hairline one would be unusable — so there it stays `w-64`
 * (the host's inline width binding goes null, letting the class through) and the handle is hidden.
 *
 * Menu items are filtered on BOTH axes — the client's features and the user's permissions — and both
 * services read the session signal, so switching client re-evaluates the menu with no reload. A
 * client-specific section (Car Shows) disappears entirely for clients that do not have it, including
 * for a Super Admin: entitlement is not a permission and is not bypassed by one.
 *
 * On top of that, the user's own arrangement — what they hid and how they ordered it — is applied by
 * NavigationPreferenceService, which owns the whole computation. That one is stored per user per
 * client in the DATABASE rather than per browser, so it follows the person between machines; width
 * and collapse below stay local, because they are about the window rather than about the menu.
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, IonBadge, IonIcon],
  host: {
    class:
      'w-64 shrink-0 max-md:absolute max-md:inset-y-0 max-md:left-0 max-md:z-40 max-md:shadow-lg',
    '[class.hidden]': '!open()',
    '[style.width.px]': 'wide() ? width() : null',
  },
  styles: `
    /* The host is the flex row holding the menu and its drag handle. Both are declared here rather
       than as utility classes so that .hidden — which the shell toggles — beats them outright,
       instead of depending on the order Tailwind happens to emit its display utilities in. */
    :host {
      display: flex;
    }

    :host(.hidden) {
      display: none;
    }
  `,
  template: `
    <nav
      class="min-w-0 flex-1 overflow-y-auto bg-white max-md:border-r max-md:border-gray-200"
      aria-label="Main"
    >
      <div class="flex justify-end border-b border-gray-200 px-2 py-1">
        <button
          type="button"
          class="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
          (click)="toggleAll()"
        >
          <ion-icon
            [name]="allCollapsed() ? 'chevron-down-outline' : 'chevron-up-outline'"
            class="text-sm"
          ></ion-icon>
          <span class="truncate">{{ allCollapsed() ? 'Expand all' : 'Collapse all' }}</span>
        </button>
      </div>

      @for (section of sections(); track section.name) {
        <div class="py-2">
          <button
            type="button"
            class="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase hover:bg-gray-100"
            [attr.aria-expanded]="!isCollapsed(section.name)"
            [attr.aria-controls]="section.id"
            (click)="toggleSection(section.name)"
          >
            <ion-icon
              [name]="
                isCollapsed(section.name) ? 'chevron-forward-outline' : 'chevron-down-outline'
              "
              class="shrink-0 text-sm"
            ></ion-icon>
            <span class="truncate">{{ section.name }}</span>
          </button>

          @if (!isCollapsed(section.name)) {
            <div [id]="section.id">
              @for (item of section.items; track item.path) {
                <a
                  [routerLink]="item.path"
                  routerLinkActive="bg-primary/10 text-primary font-semibold"
                  class="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  [title]="item.label"
                  (click)="navigated.emit()"
                >
                  <ion-icon [name]="item.icon" class="shrink-0 text-lg"></ion-icon>
                  <span class="truncate">{{ item.label }}</span>
                  <!-- Pushed to the far end with ml-auto so it reads as a count against the row
                       rather than as part of the label, and only drawn when there is something to
                       say — a badge showing 0 is noise that teaches people to stop looking. -->
                  @if (badges.count(item.badge) > 0) {
                    <ion-badge color="danger" class="ml-auto shrink-0">{{
                      badges.count(item.badge)
                    }}</ion-badge>
                  }
                </a>
              }
            </div>
          }
        </div>
      }
    </nav>

    <!-- Drag to resize; double-click (or Home) to reset. A focusable separator, so the width is
         reachable without a pointer. Hidden on a phone, where the sidebar is a fixed-width drawer. -->
    <div
      class="w-1.5 shrink-0 cursor-col-resize touch-none bg-gray-200 transition-colors hover:bg-primary/60 focus-visible:bg-primary focus-visible:outline-none max-md:hidden"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize menu"
      tabindex="0"
      [attr.aria-valuenow]="width()"
      [attr.aria-valuemin]="minWidth"
      [attr.aria-valuemax]="maxWidth"
      (pointerdown)="startResize($event)"
      (pointermove)="onResize($event)"
      (pointerup)="endResize($event)"
      (pointercancel)="endResize($event)"
      (dblclick)="resetWidth()"
      (keydown.arrowleft)="nudge($event, -16)"
      (keydown.arrowright)="nudge($event, 16)"
      (keydown.home)="resetWidth()"
    ></div>
  `,
})
export class SidebarComponent {
  private readonly navigation = inject(NavigationPreferenceService);

  /**
   * Public so the template can read counts directly.
   *
   * <p>`start()` here rather than in the shell because the sidebar is the only thing that renders a
   * badge — polling for a count nothing is drawing would be pure cost. It is idempotent, which
   * matters: this component is rebuilt when the drawer re-opens on a phone, and a second interval
   * would double every request for the rest of the session.</p>
   */
  protected readonly badges = inject(NavBadgeService);

  /** The shell owns open/closed — this only needs it to hide itself. */
  readonly open = input(true);

  /** A tap on a link; the shell uses it to dismiss the overlaying drawer. */
  readonly navigated = output<void>();

  protected readonly wide = inject(ViewportService).isWide;

  protected readonly minWidth = MIN_WIDTH;
  protected readonly maxWidth = MAX_WIDTH;

  protected readonly width = signal(readStoredWidth());

  /**
   * Holds names of sections that may not be visible right now — a section can disappear when you
   * switch to a client whose roles do not reach it, and coming back should find it as you left it.
   * That is why collapse-all adds/removes only the VISIBLE names rather than replacing the set.
   */
  private readonly collapsed = signal<ReadonlySet<string>>(readStoredCollapsed());

  /**
   * What to draw. Feature and permission filtering, plus this user's own hiding and ordering, all
   * live in NavigationPreferenceService — the settings screen that EDITS the arrangement has to
   * compute the same list, and two copies of that filter would drift the first time a fourth axis
   * appeared.
   */
  protected readonly sections = this.navigation.sections;

  protected readonly allCollapsed = computed(() => {
    const collapsed = this.collapsed();
    const sections = this.sections();

    return sections.length > 0 && sections.every((section) => collapsed.has(section.name));
  });

  private resizeStart: { x: number; width: number } | null = null;

  constructor() {
    // Begins the badge poll. Idempotent, and gated inside on feature + permission, so for a client
    // with no Rigging it never issues a request at all.
    this.badges.start();

    // Every `icon:` in NAV_ITEMS must appear here. Ionicons does not fall back: an unregistered name
    // logs a warning, tries to fetch the SVG by URL, and dies with "Failed to construct 'URL'" —
    // leaving a blank space where the nav icon should be. Nothing fails the build, so adding a nav
    // item and forgetting this line is invisible until someone opens that section. That is exactly
    // how the Fuel Tax items (navigate / car / bus / water / barbell) and Campaigns (megaphone)
    // shipped without theirs.
    addIcons({
      albumsOutline,
      notificationsOutline,
      optionsOutline,
      peopleOutline,
      barbellOutline,
      // The general ledger. Registered here because an unregistered Ionicon renders BLANK rather
      // than failing — the nav entry would ship with an invisible icon and nothing would say so.
      bookOutline,
      briefcaseOutline,
      busOutline,
      starOutline,
      businessOutline,
      calendarOutline,
      createOutline,
      gitNetworkOutline,
      gridOutline,
      callOutline,
      carOutline,
      carSportOutline,
      cardOutline,
      cashOutline,
      powerOutline,
      pulseOutline,
      chatbubblesOutline,
      checkboxOutline,
      chevronDownOutline,
      chevronForwardOutline,
      chevronUpOutline,
      colorPaletteOutline,
      clipboardOutline,
      cloudDownloadOutline,
      cloudUploadOutline,
      constructOutline,
      cubeOutline,
      desktopOutline,

      // SINGULAR, and it is not a duplicate of documentsOutline below. 'Public site -> Pages' asks
      // for 'document-outline' and this was the one nav icon in the file registered nowhere, so it
      // rendered blank from the day it shipped — found by a console warning while driving the app,
      // not by anyone noticing the gap in the menu.
      //
      // Registering the singular rather than repointing Pages at the plural, because the plural is
      // already 'Invoice templates' — one icon on two unrelated entries is a worse answer than an
      // import.
      documentOutline,
      documentTextOutline,

      // Registered alongside the import, because an unregistered Ionicon renders BLANK rather than
      // failing — the nav entry would ship with an invisible icon and nothing would say so. Both
      // earlier phases of this work hit exactly that.
      documentsOutline,

      // Automations. Registered the same day it was added, because driving the app printed the
      // warning this block's own remarks predict — "Could not load icon with name flash-outline" —
      // and the nav entry had already shipped with a blank space beside it.
      flashOutline,
      funnelOutline,
      gitBranchOutline,
      gitCompareOutline,
      globeOutline,
      hammerOutline,
      sparklesOutline,
      idCardOutline,
      imagesOutline,
      // Administration > Setup.
      checkmarkDoneOutline,
      keyOutline,
      // Fast entry. An unregistered Ionicon renders BLANK rather than failing.
      keypadOutline,
      linkOutline,
      listOutline,
      logInOutline,
      lockClosedOutline,
      locationOutline,
      mailOutline,
      mapOutline,
      megaphoneOutline,
      menuOutline,
      navigateOutline,
      speedometerOutline,
      peopleCircleOutline,
      personOutline,
      pricetagOutline,
      pricetagsOutline,
      printOutline,
      receiptOutline,
      repeatOutline,
      ribbonOutline,

      // The two general-ledger statements. Both shipped with a BLANK icon: Ionicons does
      // not fall back, so an unregistered name logs a warning and leaves a gap in the
      // menu, and nothing fails the build. Found by driving the app, which is the only
      // way this block's own remarks say it ever gets found.
      scaleOutline,
      trendingUpOutline,
      sendOutline,
      shieldOutline,
      // GL posting. An unregistered Ionicon renders BLANK rather than failing.
      swapHorizontalOutline,
      toggleOutline,
      trashOutline,
      walletOutline,
      waterOutline,
    });

    effect(() => writeStoredWidth(this.width()));
    effect(() => writeStoredCollapsed(this.collapsed()));
  }

  protected isCollapsed(name: string): boolean {
    return this.collapsed().has(name);
  }

  protected toggleSection(name: string): void {
    this.collapsed.update((current) => {
      const next = new Set(current);
      if (!next.delete(name)) next.add(name);
      return next;
    });
  }

  /** One control for both directions: anything still open collapses; everything closed expands. */
  protected toggleAll(): void {
    const collapsing = !this.allCollapsed();
    const names = this.sections().map((section) => section.name);

    this.collapsed.update((current) => {
      const next = new Set(current);
      for (const name of names) {
        if (collapsing) next.add(name);
        else next.delete(name);
      }
      return next;
    });
  }

  protected startResize(event: PointerEvent): void {
    this.resizeStart = { x: event.clientX, width: this.width() };
    (event.target as Element).setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  protected onResize(event: PointerEvent): void {
    const start = this.resizeStart;
    if (!start) return;

    // The handle is on the sidebar's right edge, so the pointer's x-delta IS the width delta — no
    // doubling (unlike the centred CRUD modal, which grows from both edges at once).
    this.width.set(clampWidth(start.width + (event.clientX - start.x)));
    event.preventDefault();
  }

  protected endResize(event: PointerEvent): void {
    this.resizeStart = null;
    (event.target as Element).releasePointerCapture?.(event.pointerId);
  }

  protected nudge(event: Event, delta: number): void {
    this.width.update((width) => clampWidth(width + delta));
    event.preventDefault();
  }

  protected resetWidth(): void {
    this.width.set(DEFAULT_WIDTH);
  }
}

function clampWidth(width: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));
}

function readStoredWidth(): number {
  try {
    const raw = localStorage.getItem(WIDTH_KEY);
    const parsed = raw === null ? NaN : Number(raw);

    return Number.isFinite(parsed) ? clampWidth(parsed) : DEFAULT_WIDTH;
  } catch {
    // Private browsing, a quota error, or someone's hand-edited value. A remembered sidebar is a
    // convenience — never a reason for the app not to render.
    return DEFAULT_WIDTH;
  }
}

function writeStoredWidth(width: number): void {
  try {
    localStorage.setItem(WIDTH_KEY, String(width));
  } catch {
    // As above.
  }
}

function readStoredCollapsed(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    if (!raw) return new Set();

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();

    return new Set(parsed.filter((name): name is string => typeof name === 'string'));
  } catch {
    return new Set();
  }
}

function writeStoredCollapsed(collapsed: ReadonlySet<string>): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]));
  } catch {
    // As above.
  }
}
